import {
  Alert,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputLabel,
  Stack,
  Typography,
  Box
} from "@mui/material";
import React from "react";
import whiteLogo from "../../assets/images/whiteLogo-bgwhite.png";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import TextFieldWrapper from "../../components/FormComponents/TextFieldWrapper";
import { Navigate, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import AuthQuery from "../../stateQueries/Auth";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";

const LoginUser = ({ currentTheme }) => {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["userInfo"],
    queryFn: async () => {
      return await AuthQuery.isUserLoggedIn();
    }
  });

  const authQuery = useMutation({
    mutationFn: (formData) => {
      return AuthQuery.loginUser(formData);
    },
    onSuccess: (data) => {
      localStorage.setItem("authToken", data?.user?.token);
      localStorage.setItem("refreshToken", data?.user?.refreshToken);
      window.location.href = `${process.env.REACT_APP_PUBLIC_URL}`;
    },
    onError: (err) => {
      console.log(err);
    }
  });

  if (data) {
    return <Navigate to="/" />;
  }

  return (
    <Stack
      direction="row"
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #3a9bfb 0%, #163683 50%, #0d2456 100%)",
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* Left Side - Branding */}
      <Stack
        flex={1}
        justifyContent="center"
        alignItems="center"
        sx={{
          display: { xs: "none", md: "flex" },
          position: "relative",
          zIndex: 1
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            animation: "floatAnimation 3s ease-in-out infinite"
          }}
        >
          <img
            src={whiteLogo}
            alt="Logo"
            style={{
              height: "120px",
              marginBottom: "2rem",
              filter: "drop-shadow(0 4px 15px rgba(0,0,0,0.15))"
            }}
          />
          <Typography
            variant="h3"
            sx={{
              color: "white",
              fontWeight: 700,
              mb: 2,
              textShadow: "0 2px 10px rgba(0,0,0,0.2)"
            }}
          >
            Welcome Back
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "rgba(255,255,255,0.95)",
              maxWidth: "400px",
              lineHeight: 1.6
            }}
          >
            Sign in to access your FASSET Content Management System
          </Typography>
        </Box>
      </Stack>

      {/* Right Side - Form */}
      <Stack
        flex={1}
        justifyContent="center"
        alignItems="center"
        sx={{ position: "relative", zIndex: 1, px: 2 }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: "480px",
            p: 5,
            position: "relative"
          }}
        >
          {authQuery.error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: "12px",
                backgroundColor:
                  currentTheme === "light"
                    ? "rgba(211, 47, 47, 0.1)"
                    : "rgba(244, 67, 54, 0.15)"
              }}
            >
              {authQuery?.error?.response?.data?.message ||
                authQuery?.error?.message}
            </Alert>
          )}
          {authQuery.isSuccess && (
            <Alert
              severity="success"
              sx={{
                mb: 3,
                borderRadius: "12px",
                backgroundColor:
                  currentTheme === "light"
                    ? "rgba(46, 125, 50, 0.1)"
                    : "rgba(76, 175, 80, 0.15)"
              }}
            >
              {authQuery?.data?.message}
            </Alert>
          )}

          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: currentTheme === "light" ? "text.primary" : "white",
                mb: 1
              }}
            >
              Sign In
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color:
                  currentTheme === "light"
                    ? "text.secondary"
                    : "rgba(255,255,255,0.7)"
              }}
            >
              Enter your credentials to continue
            </Typography>
          </Box>

          <Formik
            initialValues={{
              email: "",
              password: ""
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string().required("Please provide email"),
              password: Yup.string().required("Please provide password")
            })}
            onSubmit={(values) => {
              authQuery.mutate(values);
            }}
          >
            {() => {
              return (
                <Form>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <InputLabel
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          color:
                            currentTheme === "light" ? "text.primary" : "white"
                        }}
                      >
                        Email/Username
                      </InputLabel>
                      <TextFieldWrapper
                        name="email"
                        placeholder="Enter your email"
                        fullWidth
                        startIcon={<EmailIcon sx={{ color: "primary.main" }} />}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor:
                              currentTheme === "light"
                                ? "rgba(0,0,0,0.02)"
                                : "rgba(255,255,255,0.05)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor:
                                currentTheme === "light"
                                  ? "rgba(0,0,0,0.04)"
                                  : "rgba(255,255,255,0.08)"
                            }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <InputLabel
                          sx={{
                            fontWeight: 600,
                            color:
                              currentTheme === "light"
                                ? "text.primary"
                                : "white"
                          }}
                        >
                          Password
                        </InputLabel>
                        <Typography
                          onClick={() => {
                            navigate("/forgotPassword");
                          }}
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "primary.main",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              textDecoration: "underline"
                            }
                          }}
                        >
                          Forgot Password?
                        </Typography>
                      </Stack>
                      <TextFieldWrapper
                        name="password"
                        placeholder="Enter your password"
                        type="password"
                        fullWidth
                        startIcon={<LockIcon sx={{ color: "primary.main" }} />}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            backgroundColor:
                              currentTheme === "light"
                                ? "rgba(0,0,0,0.02)"
                                : "rgba(255,255,255,0.05)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor:
                                currentTheme === "light"
                                  ? "rgba(0,0,0,0.04)"
                                  : "rgba(255,255,255,0.08)"
                            }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            sx={{
                              color:
                                currentTheme === "light"
                                  ? "primary.main"
                                  : "rgba(255,255,255,0.7)"
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color:
                                currentTheme === "light"
                                  ? "text.secondary"
                                  : "rgba(255,255,255,0.7)"
                            }}
                          >
                            Remember Me
                          </Typography>
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        fullWidth
                        type="submit"
                        disabled={authQuery.isLoading}
                        sx={{
                          py: 1.5,
                          borderRadius: "12px",
                          fontSize: "16px",
                          fontWeight: 600,
                          textTransform: "none",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          boxShadow: "0 8px 24px rgba(102,126,234,0.4)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 12px 32px rgba(102,126,234,0.5)"
                          },
                          "&:disabled": {
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            opacity: 0.7
                          }
                        }}
                      >
                        {authQuery.isLoading ? (
                          <CircularProgress size={24} sx={{ color: "white" }} />
                        ) : (
                          "Sign In"
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              );
            }}
          </Formik>
        </Card>
      </Stack>
    </Stack>
  );
};

export default LoginUser;
